/*
 * @providesModule SignInView
 */

import React, {useState, useEffect, useMemo} from 'react';
import {
  Keyboard,
  Text,
  View,
  TouchableOpacity,
  ViewStyle,
  ImageStyle,
  StyleSheet,
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  SafeAreaView,
  BackHandler,
  NativeModules,
} from 'react-native';
import {_m} from 'INTL';
import EmailSignInView from 'EmailSignInView';
import {getStyles, createStyles} from 'SignInStyle';
import {SMSVerify} from 'SMSVerify';
import {CountryData, GenericHeader, ModalBottomSheet} from '@skillz/ui';
import source_svgs from '../../source_svgs/_source_svgs';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import SkillzSVG from 'SkillzSVG';
import SkillzStyle from 'SkillzStyle';
import {GameThemeBackground} from '../../Components/GameThemeBackground';
import WebView from 'react-native-webview';
import PushButton from 'PushButton';
import SkillzSDK from 'SkillzSDK';
import Tournament from 'SKZTournament';
import ClientPropertyConstants from 'ClientPropertyConstants';
import {sendEmailOTP} from 'EmailOTPUtils';
import BannerModal from '../BannerModal/BannerModal';
import LoadingIndicator from 'LoadingIndicator';

const SkillzModule = NativeModules.SkillzModule;

export type SignInSMSProps = {
  /** Function used to validate input for the phone number input box */
  isValidNumber: (phoneNumber: string, country: string) => boolean;
  /** Function used to request verification code */
  sendSMSVerification: (
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (errorString: string) => void,
    call?: boolean
  ) => void;
  /** function to fetch default country*/
  fetchCountry: (setCountry: React.Dispatch<React.SetStateAction<string>>) => void;
  /**  Function used to format input for the phone number input box*/
  formatNumber: (number: string, iso2: string) => string;
  /** Function used to get country code for a given country */
  getCountryCode: (country: string) => string;
  /**
   *
   * @param country Country used to get flag
   * @param style Passed in to given function to style flag
   * @returns flag image
   */
  getFlag: (country: string, style: ImageStyle) => React.ReactElement;
  /** List of countries used for the CountryListModal */
  countryData: CountryData[];
  /** Number of codeInput boxes to display/ length of verification code */
  numberOfInputs: number;
  /** Function for validating verification code inputted by user */
  codeValidation: (
    code: string,
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (errorString: string) => void
  ) => void;
  /** Loading functions show/hide*/
  loading?: {
    show: () => void;
    hide: () => void;
  };
  /** Optional container style prop*/
  containerStyle?: ViewStyle;
  /** Optional field to input the phone number*/
  inputPhoneNumber?: string;
  /** Optional bool indicating the phone number is already in use*/
  phoneNumberInUse?: boolean;
  /** Optional field to input the country*/
  inputCountry?: string;
  /** Optional field to input the country value*/
  inputCountryValue?: string;
  /** Function to redirect user to the login screen */
  onPhoneNumberInUse?: (phoneNumber: string, country: string, countryValue: string) => void;
  // When user already has an account and is in signup flow
  userAlreadyHaveAccount?: boolean;
};

export interface SignInProps {
  //Callback on “Close” button
  onClosePress: () => void;
  //Handle callback on "Sign In" button
  onSignInPress: (username: string, password: string) => void;
  //Handle Forgot Password Action
  forgotPasswordAction: (username: string) => Promise<boolean>;
  //Props for SMSVerification
  SMSProps: SignInSMSProps;
  //Tab Client Props determining what tabs to show
  tabClientProps: {email: boolean; phone: boolean; emailInitial: boolean};
  //Function to track aws events
  trackEvent: (name: string, params?: Object, metrics?: Object) => void;
  //User's email to pre-populate the field
  email?: string;
  // Sign in modal color variant
  colorVariant?: 'dark';
  // Initial tab to show
  initialTab?: TabType;
  // For FTUE revamp
  isNewUI?: boolean;
  gameTheme?: any;
  onContinueWithIDPPress?: (
    type: string,
    onDone: () => void,
    tournament: Tournament,
    navigator: any
  ) => void;
  onSignInWithEmailOTP: (
    email: string,
    token: string,
    isSignUp: boolean,
    onDone: ({data, success}: {data: any; success: boolean}) => void
  ) => void;

  // Callback for email code validation to trigger exit deposit flow (for deposit flow with email)
  emailCodeValidation?: () => void;
}

const TEST_IDS = {
  modalTestID: 'sign_in_modal',
  titleTestID: 'sign_in_title',
  closeTestID: 'sign_in_close',
};

export enum TabType {
  EMAIL = 'Email',
  PHONE = 'Phone',
}

export enum PreferedTabType {
  EMAIL = 'email',
  PHONE = 'phone',
  APPLE = 'apple',
}

export enum EmailFieldType {
  PASSWORD = 'Password',
  OTP = 'OTP',
}
const AWS_EVENTS = {
  EXIT_PRESS: 'SIGN_IN_EXIT',
  INFO_PRESS: 'SIGN_IN_INFO',
  VIEW_PHONE_TAB: 'SIGN_IN_PHONE_VIEW',
  VIEW_EMAIL_TAB: 'SIGN_IN_EMAIL_VIEW',
  AREA_CODE_BOX_PRESS: 'SIGN_IN_PHONE_AREACODE_TAP',
  SEND_CODE: 'SIGN_IN_PHONE_SEND',
  SEND_CODE_CALL: 'SIGN_IN_PHONE_CALL_SEND',
  ENTER_CODE: 'SIGN_IN_SMS_ENTER',
};

const SignInView = (props: SignInProps) => {
  const initialTab =
    props.initialTab ||
    (props.tabClientProps?.email && props.tabClientProps.emailInitial
      ? TabType.EMAIL
      : TabType.PHONE);

  const [activeTab, setActiveTab] = useState(initialTab);

  const [showTos, setShowTos] = useState(false);
  // Initialize verificationCode based on userAlreadyHaveAccount to prevent flash when navigating from signup
  const [verificationCode, setVerificationCode] = useState(
    props.SMSProps.userAlreadyHaveAccount && props.SMSProps.inputPhoneNumber ? true : false
  );
  const [visible, setVisible] = useState(false);
  const [isEmailVerificationActive, setIsEmailVerificationActive] = useState(false);
  const showTabBar = props.tabClientProps.email && props.tabClientProps.phone;
  const styles = getStyles(props.colorVariant);
  const [webViewLoading, setWebViewLoading] = useState(false);

  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;
  const style = createStyles(SkillzStyle.getOverriddenFallbackTheme(), isDarkTheme);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [emailFieldType, setEmailFieldType] = useState(EmailFieldType.PASSWORD);
  const [otpAlreadySent, setOtpAlreadySent] = useState(false);
  // check if email/otp is present in SkillzModule.java
  const isEmailLoginEnabled =
    typeof (SkillzModule as any).loginUserWithEmail === 'function' ? true : false;

  const [preferedMethod, setPreferedMethod] = useState(
    isEmailLoginEnabled
      ? ClientPropertyConstants.getFTUEPreferredSignupMethod()
      : PreferedTabType.PHONE
  );

  const [emailUsername, setEmailUsername] = useState('');
  const [password, setPassword] = useState('');

  const {btnTextColorOne, textColorPrimary, playButtonGradient} =
    SkillzStyle.getOverriddenFallbackTheme();

  //Error Banner Visible
  const [errorBannerVisible, setErrorBannerVisible] = useState({visible: false, message: ''});
  const [successBannerVisible, setSuccessBannerVisible] = useState({visible: false, message: ''});

  const handleEmailOtp: () => Promise<boolean> = async () => {
    setErrorBannerVisible({visible: false, message: ''});
    setSuccessBannerVisible({visible: false, message: ''});

    if (!isEmailLoginEnabled) {
      return true;
    }

    const result = await sendEmailOTP({
      email: emailUsername,
      isSignUp: false,
      displayName: emailUsername,
    });
    if (!result.success) {
      setErrorBannerVisible({visible: true, message: result.message});
      return result.success;
    } else {
      setOtpAlreadySent(true); // mark OTP has been sent
      setSuccessBannerVisible({visible: true, message: 'OTP sent successfully!'});
      return result.success;
    }
  };

  const selectEmailFieldType = () => {
    if (emailFieldType === EmailFieldType.PASSWORD && isValidEmail) {
      setEmailFieldType(EmailFieldType.OTP);
      // only send OTP if it isn't sent already
      if (!otpAlreadySent) {
        handleEmailOtp();
      }
    } else if (emailFieldType === EmailFieldType.OTP && isValidEmail) {
      setEmailFieldType(EmailFieldType.PASSWORD);
    } else {
      // For invalid emails, always show password field
      setEmailFieldType(EmailFieldType.PASSWORD);
    }
  };

  useEffect(() => {
    if (props.SMSProps.userAlreadyHaveAccount && props.SMSProps.inputPhoneNumber) {
      // verificationCode is now initialized in useState to prevent flash
      setPreferedMethod(PreferedTabType.PHONE);
    }
  }, []);

  // to show only Password field if username is entered
  useEffect(() => {
    if (isValidEmail && isEmailLoginEnabled) {
      setEmailFieldType(EmailFieldType.OTP);
      // handleEmailOtp();
    } else {
      setEmailFieldType(EmailFieldType.PASSWORD);
    }
  }, [isValidEmail, isEmailLoginEnabled]);

  useEffect(() => {
    if (!props.tabClientProps.phone) {
      setActiveTab(TabType.EMAIL);
    }
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_IN_PAGE_LOAD,
    });
  }, []);

  useEffect(() => {
    if (activeTab === TabType.EMAIL) {
      props.trackEvent(AWS_EVENTS.VIEW_EMAIL_TAB);
      setVerificationCode(false);
    } else {
      props.trackEvent(AWS_EVENTS.VIEW_PHONE_TAB);
    }
  }, [activeTab]);

  useEffect(() => {
    if (showTos) {
      Keyboard.dismiss();
    }
  }, [showTos]);

  useEffect(() => {
    if (preferedMethod === PreferedTabType.APPLE) {
      handleSocialEmailLinking();
    }
  }, [preferedMethod]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      BackHandler.addEventListener('hardwareBackPress', () => {
        // if (props?.isNewUI) {
        // backPressHandlerForFTUE();
        // }
        return true;
      });
    }

    return () => {
      if (Platform.OS !== 'web') {
        BackHandler.removeEventListener('hardwareBackPress', () => undefined);
      }
    };
  }, []);

  const renderErrorBanner = useMemo(() => {
    if (errorBannerVisible.visible) {
      return (
        <View style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000}}>
          <BannerModal
            title={_m('Error')}
            message={_m(errorBannerVisible?.message)}
            numberOfLines={2}
            error={true}
            heightContainer={90}
            onClosePress={() => setErrorBannerVisible({visible: false, message: ''})}
          />
        </View>
      );
    } else if (successBannerVisible.visible) {
      return (
        <View style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000}}>
          <BannerModal
            title={_m('Success')}
            message={_m(successBannerVisible?.message)}
            numberOfLines={2}
            error={false}
            heightContainer={90}
            onClosePress={() => setSuccessBannerVisible({visible: false, message: ''})}
          />
        </View>
      );
    }
    return false;
  }, [errorBannerVisible, successBannerVisible]);

  const handleSocialEmailLinking = async () => {
    try {
      if (props?.onContinueWithIDPPress) {
        const error = await props?.onContinueWithIDPPress(
          'apple',
          null,
          null,
          SkillzSDK.getNavigator()
        );
      }
    } catch (error) {
      console.warn('Error in federated login press', error);
    }
  };

  const renderSignInContainer = () => {
    return (
      <View style={props?.isNewUI ? {} : styles.containerScrollview}>
        <EmailSignInView
          colorVariant={props.colorVariant}
          onSignInPress={props.onSignInPress}
          forgotPasswordAction={props.forgotPasswordAction}
          tosValues={{showTos, setShowTos}}
          loading={{
            show: () => props.SMSProps.loading?.show(),
            hide: () => props.SMSProps.loading?.hide(),
          }}
          email={props.email}
          isNewUI={props?.isNewUI}
          gameTheme={props.gameTheme}
          onEmailVerificationStateChange={setIsEmailVerificationActive}
          fieldType={emailFieldType}
          time={otpAlreadySent ? 30 : emailFieldType === EmailFieldType.OTP ? 30 : 15}
          setEmailUsername={(email: string) => {
            setEmailUsername(email);
            // Reset OTP sent flag when email changes
            if (email !== emailUsername) {
              setOtpAlreadySent(false);
            }
          }}
          handlePassword={setPassword}
          header={{
            title: _m('Existing User Login'),
            description: _m('Please enter your email address or username'),
          }}
          isEmailVerificationActive={isEmailVerificationActive}
          onSignInWithEmailOTP={props.onSignInWithEmailOTP}
          setIsValidEmail={setIsValidEmail}
          onOtpMethodSelected={handleEmailOtp}
          onError={(error: string) => {
            setErrorBannerVisible({visible: true, message: error});
            setIsEmailVerificationActive(false); // go back to email input screen
            setIsValidEmail(false); // reset email validation state
            setOtpAlreadySent(false); // reset OTP sent flag on error
          }}
          codeValidation={props.emailCodeValidation}
        />
      </View>
    );
  };
  const renderTab = () => {
    const phoneTextStyle = activeTab === TabType.PHONE ? styles.activeTab : styles.inActiveTab;
    const emailTextStyle = activeTab === TabType.EMAIL ? styles.activeTab : styles.inActiveTab;
    return (
      <View style={styles.tabContainer}>
        <TouchableOpacity
          onPress={() => {
            trackTelemetryEvent({
              event: TelemetryEventName.SIGN_IN_CTA_CLICK,
              payload: {cta: 'phone'},
            });
            setActiveTab(TabType.PHONE);
            setShowTos(false);
          }}
          style={[
            styles.tab,
            {
              borderBottomColor:
                activeTab === TabType.PHONE
                  ? styles.activeTab.borderBottomColor
                  : styles.inActiveTab.borderBottomColor,
            },
          ]}
        >
          <Text allowFontScaling={false} style={{...phoneTextStyle, ...styles.tabText}}>
            {_m('Phone')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            trackTelemetryEvent({
              event: TelemetryEventName.SIGN_IN_CTA_CLICK,
              payload: {cta: 'email/username'},
            });
            setActiveTab(TabType.EMAIL);
            setShowTos(false);
          }}
          style={[
            styles.tab,
            {
              borderBottomColor:
                activeTab === TabType.EMAIL
                  ? styles.activeTab.borderBottomColor
                  : styles.inActiveTab.borderBottomColor,
            },
          ]}
        >
          <Text allowFontScaling={false} style={{...emailTextStyle, ...styles.tabText}}>
            {_m('Email/Username')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  const handleSignInPress = () => {
    if (emailUsername.length) {
      props.onSignInPress(emailUsername, password);
    }
  };

  const backPressHandlerForFTUE = () => {
    if (
      verificationCode &&
      !(props.SMSProps.userAlreadyHaveAccount && props.SMSProps.inputPhoneNumber)
    ) {
      setVerificationCode(false);
    } else if (preferedMethod === PreferedTabType.EMAIL && isEmailVerificationActive) {
      setIsEmailVerificationActive(false);
      setIsValidEmail(false);
      isEmailLoginEnabled && isValidEmail
        ? setEmailFieldType(EmailFieldType.OTP)
        : setEmailFieldType(EmailFieldType.PASSWORD);
    } else {
      props.trackEvent(AWS_EVENTS.EXIT_PRESS);
      props.onClosePress();
    }
  };

  const renderSMS = (smsProps: SignInSMSProps) => {
    return (
      <SMSVerify
        colorVariant={props.colorVariant}
        messageText={{
          header: _m('Existing User Login'),
          body: _m('Please enter your mobile number to get a 5-digit code'),
        }}
        isValidNumber={smsProps.isValidNumber}
        sendSMSVerification={smsProps.sendSMSVerification}
        fetchCountry={smsProps.fetchCountry}
        formatNumber={smsProps.formatNumber}
        getCountryCode={smsProps.getCountryCode}
        getFlag={smsProps.getFlag}
        countryData={smsProps.countryData}
        numberOfInputs={smsProps.numberOfInputs}
        codeValidation={smsProps.codeValidation}
        showTosMessage={true}
        showSmsMarketingCheckbox={false}
        tosValues={{showTos, setShowTos}}
        loading={{
          show: () => smsProps.loading?.show(),
          hide: () => smsProps.loading?.hide(),
        }}
        events={{
          countrySelect: AWS_EVENTS.AREA_CODE_BOX_PRESS,
          codeRequest: AWS_EVENTS.SEND_CODE,
          callRequest: AWS_EVENTS.SEND_CODE_CALL,
          codeEnter: AWS_EVENTS.ENTER_CODE,
        }}
        phoneNumberInUse={props.SMSProps.phoneNumberInUse}
        trackEvent={props.trackEvent}
        verificationCodeValues={{verificationCode, setVerificationCode}}
        inputPhoneNumber={props.SMSProps.inputPhoneNumber}
        inputCountry={props.SMSProps.inputCountry}
        inputCountryValue={props.SMSProps.inputCountryValue}
        onPhoneNumberInUse={props.SMSProps.onPhoneNumberInUse}
        isNewUI={props?.isNewUI}
        gameTheme={props.gameTheme}
        userAlreadyHaveAccount={props.SMSProps.userAlreadyHaveAccount}
        onClosePress={props.onClosePress}
      />
    );
  };

  const renderHelpModal = () => {
    const helpModalTitle =
      activeTab === TabType.EMAIL
        ? _m('Sign in with your email or username.')
        : _m('Sign in with your phone number.');
    const helpModalBodyBeginning =
      activeTab === TabType.EMAIL
        ? _m('Sign in to your Skillz account with your email or username.')
        : _m('Sign in to your Skillz account with your mobile phone number.');
    const helpModalBodyEnd =
      activeTab === TabType.EMAIL
        ? _m(
            'If you did not sign up with your email, then you must sign in with your phone number by tapping “Phone”.'
          )
        : _m(
            'If you have not provided your phone number, then you can still sign in with your email or username by tapping “Email / Username”.'
          );
    const helpModalBody =
      props.tabClientProps.phone && props.tabClientProps.email
        ? helpModalBodyBeginning + helpModalBodyEnd
        : helpModalBodyBeginning;
    return (
      <View>
        <ModalBottomSheet
          additionalFooterStyles={styles.helpModalContentWrapper}
          visible={visible}
          maxHeight={500}
          onRequestClose={() => setVisible(false)}
          footerText={_m('Close')}
          footerOnPress={() => setVisible(false)}
          footerColor={'#4890E8'}
        >
          <View style={styles.helpModalContainer}>
            <Text allowFontScaling={false} style={styles.helpModalTitle}>
              {helpModalTitle}
            </Text>
            <Text allowFontScaling={false} style={styles.helpModalBody}>
              {helpModalBody}
            </Text>
          </View>
        </ModalBottomSheet>
      </View>
    );
  };

  if (props?.isNewUI) {
    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={style.container}
      >
        <GameThemeBackground />
        {renderErrorBanner}
        <View style={style.container}>
          {!showTos && (
            <View style={style.header}>
              <TouchableOpacity onPress={backPressHandlerForFTUE}>
                <SkillzSVG
                  source={isDarkTheme ? 'arrow_left.svg' : 'arrow-left_black.svg'}
                  style={style.backIcon}
                  resizeMode='contain'
                />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  setVisible(true);
                  props.trackEvent(AWS_EVENTS.INFO_PRESS);
                }}
              >
                <SkillzSVG
                  source={isDarkTheme ? 'help_circle.svg' : 'help-circle_black.svg'}
                  style={style.infoIcon}
                  tintColor={textColorPrimary.color}
                  fill='transparent'
                  resizeMode='contain'
                />
              </TouchableOpacity>
            </View>
          )}

          <ScrollView
            contentContainerStyle={style.scrollViewContent}
            showsVerticalScrollIndicator={false}
          >
            {preferedMethod === PreferedTabType.PHONE && props.SMSProps
              ? renderSMS(props.SMSProps)
              : null}
            {preferedMethod === PreferedTabType.EMAIL || preferedMethod === PreferedTabType.APPLE
              ? renderSignInContainer()
              : null}

            {/* Hide this View only when in SMS verification code input state */}
            {!(
              (preferedMethod === PreferedTabType.PHONE && verificationCode) ||
              (preferedMethod === PreferedTabType.EMAIL && isEmailVerificationActive)
            ) && (
              <View>
                <View style={style.orContinueWithContainer}>
                  <SkillzSVG
                    source={isDarkTheme ? 'rightLine.svg' : 'leftBlack.svg'}
                    style={style.line}
                  />
                  <Text allowFontScaling={false} style={style.orContinueWithText}>
                    {_m('Or continue with')}
                  </Text>
                  <SkillzSVG
                    source={isDarkTheme ? 'leftLine.svg' : 'rightBlack.svg'}
                    style={style.line}
                  />
                </View>

                {/* Social Login Buttons */}
                <View style={style.socialButtonsContainer}>
                  <View style={style.buttonWrapper}>
                    <TouchableOpacity
                      style={style.socialButton}
                      onPress={() =>
                        preferedMethod === PreferedTabType.PHONE
                          ? setPreferedMethod(PreferedTabType.EMAIL)
                          : setPreferedMethod(PreferedTabType.PHONE)
                      }
                    >
                      <SkillzSVG
                        source={
                          preferedMethod === PreferedTabType.EMAIL
                            ? isDarkTheme
                              ? 'mobile.svg'
                              : 'mobile_white.svg'
                            : isDarkTheme
                              ? 'mail.svg'
                              : 'mail_white.svg'
                        }
                        style={style.socialIcon}
                        resizeMode='contain'
                      />
                      <Text allowFontScaling={false} style={style.socialButtonText}>
                        {preferedMethod === PreferedTabType.EMAIL ||
                        preferedMethod === PreferedTabType.APPLE
                          ? _m('Phone number')
                          : preferedMethod === PreferedTabType.PHONE
                            ? _m('Email / Username')
                            : _m('Email / Username')}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {Platform.select({ios: true, macos: true}) &&
                    ClientPropertyConstants.getAppleSignUpEnabledIOS() && (
                      <View style={style.buttonWrapper}>
                        <TouchableOpacity
                          style={style.socialButton}
                          onPress={handleSocialEmailLinking}
                        >
                          <SkillzSVG
                            source={isDarkTheme ? 'apple.svg' : 'apple_white.svg'}
                            style={style.socialIcon}
                            resizeMode='contain'
                          />
                          <Text allowFontScaling={false} style={style.socialButtonText}>
                            {_m('Apple account')}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    )}
                </View>

                <Text allowFontScaling={false} style={style.normalText}>
                  {_m('By continuing, you agree to our')}{' '}
                  <Text
                    allowFontScaling={false}
                    style={style.linkText}
                    onPress={() => {
                      setWebViewLoading(true);
                      setShowTos(true);
                    }}
                  >
                    {_m('User Terms and Conditions')}
                  </Text>{' '}
                  {_m('of Service and confirm that you are at least 18 years old.')}
                </Text>
              </View>
            )}

            {/* show this view only when in email code verification input state for password/otp */}
            {preferedMethod === PreferedTabType.EMAIL && isEmailVerificationActive && (
              <View>
                <PushButton
                  colors={playButtonGradient}
                  title={_m('Next')}
                  onPress={handleSignInPress}
                  cornerRadius={12}
                  textStyle={{
                    color: btnTextColorOne.color,
                    fontSize: 18,
                    ...SkillzStyle.fullStyles.fontBold,
                  }}
                  style={{
                    height: 56,
                    opacity: !(emailFieldType === EmailFieldType.PASSWORD && password?.length > 3)
                      ? 0.6
                      : 1,
                  }}
                  disabled={!(emailFieldType === EmailFieldType.PASSWORD && password?.length > 3)}
                />

                {isValidEmail && isEmailLoginEnabled && (
                  <>
                    <View style={style.orContinueWithContainer}>
                      <SkillzSVG
                        source={isDarkTheme ? 'rightLine.svg' : 'leftBlack.svg'}
                        style={style.line}
                      />
                      <Text allowFontScaling={false} style={style.orContinueWithText}>
                        {_m('Or')}
                      </Text>
                      <SkillzSVG
                        source={isDarkTheme ? 'leftLine.svg' : 'rightBlack.svg'}
                        style={style.line}
                      />
                    </View>

                    <Text
                      allowFontScaling={false}
                      onPress={selectEmailFieldType}
                      style={style.loginWithText}
                    >
                      {_m('Login with')}{' '}
                      {emailFieldType === EmailFieldType.PASSWORD
                        ? EmailFieldType?.OTP
                        : EmailFieldType.PASSWORD}
                    </Text>
                  </>
                )}
              </View>
            )}
          </ScrollView>

          {showTos && (
            <SafeAreaView style={style.tosView}>
              <View style={style.tosHeaderContainer}>
                <GameThemeBackground />
                <Text allowFontScaling={false} style={style.tosTitle}>
                  {_m('Terms of Use')}
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    setShowTos(false);
                    setWebViewLoading(false);
                  }}
                >
                  <Text
                    allowFontScaling={false}
                    style={[style.tosCloseButton, {color: btnTextColorOne.color}]}
                  >
                    {_m('Close')}
                  </Text>
                </TouchableOpacity>
              </View>
              {webViewLoading && (
                <View style={[StyleSheet.absoluteFillObject, {top: 70}]}>
                  <LoadingIndicator
                    titleText=''
                    indicatorStyle={LoadingIndicator.Style.FullScreen}
                    isInline={false}
                    testID='webview-loading-indicator'
                  />
                </View>
              )}
              <WebView
                startInLoadingState
                onLoadStart={() => setWebViewLoading(true)}
                onLoadEnd={() => setWebViewLoading(false)}
                onError={() => setWebViewLoading(false)}
                source={{uri: 'https://skillz.com/legal'}}
              />
            </SafeAreaView>
          )}
        </View>
        {renderHelpModal()}
      </KeyboardAvoidingView>
    );
  }

  return (
    <View style={styles.container} testID={TEST_IDS.modalTestID}>
      <View style={styles.headerContainer}>
        <GenericHeader
          title={_m('Sign In')}
          leftIcon={source_svgs['arrow.svg']}
          onLeftPress={() => {
            if (showTos) {
              setShowTos(false);
            } else if (
              verificationCode &&
              !(props.SMSProps.userAlreadyHaveAccount && props.SMSProps.inputPhoneNumber)
            ) {
              setVerificationCode(false);
            } else {
              props.trackEvent(AWS_EVENTS.EXIT_PRESS);
              props.onClosePress();
            }
          }}
          rightIcon={source_svgs['helpIcon.svg']}
          onRightPress={() => {
            setVisible(true);
            props.trackEvent(AWS_EVENTS.INFO_PRESS);
            trackTelemetryEvent({
              event: TelemetryEventName.SIGN_IN_CTA_CLICK,
              payload: {cta: 'question button'},
            });
          }}
          containerStyle={
            !showTabBar || showTos
              ? {
                  borderBottomColor: 'rgba(0, 0, 0, 0.2)',
                  justifyContent: 'center',
                  borderBottomWidth: 1,
                }
              : {
                  justifyContent: 'center',
                }
          }
        />
      </View>
      {showTabBar && !showTos ? renderTab() : null}
      {activeTab === TabType.EMAIL ? renderSignInContainer() : null}
      {activeTab === TabType.PHONE && props.SMSProps ? renderSMS(props.SMSProps) : null}
      {renderHelpModal()}
    </View>
  );
};

export default SignInView;
